import { useState, useRef } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { LoadingButton } from '@atlaskit/button';
import { FormFooter } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { Flex, Inline, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { SubmitButton, SubmitButtonRef } from '@atlassian/invite-people/dist/types/types';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { PercievedProvisioningTiming } from '@/components/signup/provisioning-funnel-ufo/provisioning-funnel-ufo';
import { ufoEventOnboardingInviteUsers } from '@/components/signup/utils/ufo-events';
import { UfoCustomTimingKey } from '@/components/signup/utils/ufo.types';
import { wacToken } from '@/libraries/design/foundations';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag.ts';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest.ts';

import { ANALYTICS_PREFIX } from '../../../common/constants';
import { PageInviteLeftProps } from '../../../common/types';
import { useWizardConfiguration } from '../../../controllers';
import { onInviteUsersRedirectFailureInvite } from '../../../utils/redirect-logging';
import { useEmailInvitesInput } from '../hooks/use-email-invites-input.ts';
import { getEmailListAnalytics } from '../utils';

import { EmailInputComponent } from './email-input-component';
import { InvitePeopleWrapper } from './invite-people-wrapper';
import messages from './messages';
import { ShareLink } from './share-link';

const titleCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	h1: {
		fontFamily: wacToken('font.charlie.display'),
		fontWeight: 700,
		fontSize: '24px',
		lineHeight: '30px',
	},
});

const subtextStyles = css({
	font: token('font.body'),
	color: token('color.text.subtle'),
	margin: 0,
});

const submitButtonCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		borderRadius: '6px !important',
		paddingBlock: '6px !important',
		paddingInline: '12px !important',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		span: {
			fontFamily: `var(--atlassian-sans-font-family) !important`,
			fontSize: '14px !important',
			lineHeight: '1.5rem !important',
			fontWeight: `${token('font.weight.medium')} !important`,
		},
	},
});

const doThisLaterCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		borderRadius: '6px !important',
		paddingBlock: '6px !important',
		paddingInline: '12px !important',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		span: {
			fontFamily: `var(--atlassian-sans-font-family) !important`,
			fontSize: '14px !important',
			lineHeight: '1.5rem !important',
			fontWeight: `${token('font.weight.medium')} !important`,
			// eslint-disable-next-line local/ensure-wac-token-usage
			color: '#505258 !important',
		},
	},
});

const FAILSAFE_REDIRECT_TIMEOUT = 20000;

export const LeftHandSidePageInvite = ({ onSubmit, onClickNext }: PageInviteLeftProps) => {
	const [isInvitingUsers, setIsInvitingUsers] = useState(false);
	const [isSubmittingInvite, setIsSubmittingInvite] = useState(false);
	const [isSubmittingSkip, setIsSubmittingSkip] = useState(false);

	const [emailList, setEmailList] = useState<string[]>([]);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		teamType,
		projectDetails: { template },
	} = useWizardConfiguration();
	const { signupId } = useSearchParameters();

	const invitesRefactorEnabled = checkFeatureFlagGate(
		featureFlags.jiraOnboardingWizardRefactorInvites,
	);

	const { validatedEmails, addEmail, removeEmail } = useEmailInvitesInput();

	const submitRef = useRef<HTMLButtonElement & SubmitButton>(null);

	const renderHeading = () => {
		const titleMsg = isInvitingUsers ? messages.titleSubmitting : messages.title;
		const bodyMsg = isInvitingUsers ? messages.bodyLineOneSubmitting : messages.bodyLineOne;
		return (
			<Stack space={'space.100'}>
				<div css={titleCss}>
					<Heading as="h1" size="xlarge">
						<FormattedMessage {...titleMsg} />
					</Heading>
				</div>
				<p css={subtextStyles}>
					<FormattedMessage {...bodyMsg} />
				</p>
			</Stack>
		);
	};

	const onInviteStart = () => {
		setIsInvitingUsers(true);
	};

	const onClickNextSubmit = async () => {
		setIsSubmittingInvite(true);
		ufoEventOnboardingInviteUsers.setCustomData({ signupId });

		const { emailCount, isCrossDomain } = getEmailListAnalytics(emailList);

		await Promise.resolve(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: `${ANALYTICS_PREFIX}InviteSubmitButton`,
				eventType: 'ui',
				attributes: {
					emailCount,
					isCrossDomain,
					teamType,
					template,
				},
			}).fire(),
		);

		if (emailList.length === 0) {
			if (onClickNext) {
				onClickNext();
			}

			return;
		}

		setTimeout(() => {
			onInviteUsersRedirectFailureInvite('time-out');
			if (onClickNext) {
				onClickNext();
			}
		}, FAILSAFE_REDIRECT_TIMEOUT);

		// @ts-expect-error - submitRef.current.onClick exists at runtime but is not in the type definition
		await submitRef?.current?.onClick();
	};

	const onClickNextDoThisLater = () => {
		setIsSubmittingSkip(true);

		createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: `${ANALYTICS_PREFIX}InviteSkipButton`,
			eventType: 'ui',
		}).fire();

		if (onClickNext) {
			onClickNext();
		}
	};

	return (
		<>
			<Flex direction="column" justifyContent="space-between" xcss={containerStyles}>
				{checkFeatureFlagGate(featureFlags.provisioningFunnelUfoImprovements) &&
					isInvitingUsers && (
						<PercievedProvisioningTiming
							provisioningFunnelTimingKey={
								UfoCustomTimingKey.PERCEIVED_PROVISIONING_FUNNEL_INVITE_TEAMMATES
							}
						/>
					)}
				<Stack space="space.400">
					{renderHeading()}
					<Flex direction="column" alignItems="start">
						<ShareLink />
						{invitesRefactorEnabled ? (
							<EmailInputComponent
								validatedEmails={validatedEmails}
								addEmail={addEmail}
								removeEmail={removeEmail}
							/>
						) : (
							<InvitePeopleWrapper
								submitRef={submitRef as SubmitButtonRef}
								onClickNext={onClickNext}
								onInviteStart={onInviteStart}
								setEmailList={setEmailList}
								showEmailInput={!!onSubmit}
							/>
						)}
					</Flex>
				</Stack>
			</Flex>
			<FormFooter>
				<Flex justifyContent="end" alignItems="center" xcss={footerStyles}>
					<Inline alignBlock="center" alignInline="end" space="space.050">
						<div css={doThisLaterCss}>
							<LoadingButton
								appearance="subtle"
								onClick={() => {
									onClickNextDoThisLater();

									if (onSubmit) {
										onSubmit();
									}
								}}
								isDisabled={emailList.length > 0 || isSubmittingInvite || isInvitingUsers}
								isLoading={isSubmittingSkip}
							>
								<FormattedMessage {...messages.doThisLater} />
							</LoadingButton>
						</div>
						<div css={submitButtonCss}>
							<LoadingButton
								appearance="primary"
								onClick={() => {
									if (onSubmit) {
										onSubmit(emailList);
									} else {
										onClickNextSubmit();
									}
								}}
								isDisabled={isSubmittingSkip}
								isLoading={isInvitingUsers || isSubmittingInvite}
								ref={submitRef}
							>
								<FormattedMessage
									{...(emailList.length > 0 ? messages.inviteAndGoToJira : messages.goToJira)}
								/>
							</LoadingButton>
						</div>
					</Inline>
				</Flex>
			</FormFooter>
		</>
	);
};

const containerStyles = xcss({
	height: '100%',
});

const footerStyles = xcss({
	width: '100%',
	color: 'color.text.subtlest',
});
